import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import { useTrackOticAuth } from '@/context/TrackOticAuthContext';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  Truck, LayoutDashboard, Package, MapPin, Wifi, Bell, 
  Users, FileText, LogOut, ChevronLeft, ChevronRight, Settings
} from 'lucide-react';

export default function OpsLayout() {
  const [collapsed, setCollapsed] = useState(false);
  const { user, tenant, logout, loading, hasPermission } = useTrackOticAuth();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !user) {
      navigate('/ops/login');
    }
  }, [user, loading, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-teal-500"></div>
      </div>
    );
  }

  if (!user) return null;

  const handleLogout = () => {
    logout();
    navigate('/ops/login');
  };

  // Build menu based on permissions
  const menuItems = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, path: '/ops/dashboard', permission: null },
    { id: 'work-orders', label: 'Work Orders', icon: Package, path: '/ops/work-orders', permission: null },
    { id: 'tracking', label: 'Live Tracking', icon: MapPin, path: '/ops/tracking', permission: null },
    { id: 'devices', label: 'Device Health', icon: Wifi, path: '/ops/devices', permission: null },
    { id: 'alerts', label: 'Alerts', icon: Bell, path: '/ops/alerts', permission: null },
    { id: 'reports', label: 'Reports', icon: FileText, path: '/ops/reports', permission: null },
    { id: 'users', label: 'Users', icon: Users, path: '/ops/users', permission: null },
  ].filter(item => !item.permission || hasPermission(item.permission));

  return (
    <div className="min-h-screen bg-slate-950 flex">
      {/* Sidebar */}
      <aside className={cn(
        "bg-slate-900 border-r border-slate-800 flex flex-col transition-all duration-300",
        collapsed ? "w-16" : "w-64"
      )}>
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-slate-800">
          {!collapsed && (
            <div className="flex items-center gap-2">
              <div 
                className="w-8 h-8 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: tenant?.primary_color || '#0d9488' }}
              >
                <Truck className="w-5 h-5 text-white" />
              </div>
              <span className="font-bold text-white truncate">{tenant?.name || 'TrackOtic'}</span>
            </div>
          )}
          {collapsed && (
            <div 
              className="w-8 h-8 rounded-lg flex items-center justify-center mx-auto"
              style={{ backgroundColor: tenant?.primary_color || '#0d9488' }}
            >
              <Truck className="w-5 h-5 text-white" />
            </div>
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setCollapsed(!collapsed)}
            className="text-slate-400 hover:text-white"
            data-testid="ops-sidebar-toggle"
          >
            {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
          </Button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-2 space-y-1">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path || 
              (item.path !== '/ops/dashboard' && location.pathname.startsWith(item.path));
            return (
              <Link
                key={item.id}
                to={item.path}
                className={cn(
                  "flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors",
                  isActive 
                    ? "text-white" 
                    : "text-slate-400 hover:text-white hover:bg-slate-800"
                )}
                style={isActive ? { backgroundColor: tenant?.primary_color || '#0d9488' } : {}}
                data-testid={`ops-nav-${item.id}`}
              >
                <Icon className="w-5 h-5 flex-shrink-0" />
                {!collapsed && <span>{item.label}</span>}
              </Link>
            );
          })}
        </nav>

        {/* User section */}
        <div className="p-4 border-t border-slate-800">
          {!collapsed && (
            <div className="mb-3">
              <p className="text-white font-medium truncate">{user?.full_name}</p>
              <p className="text-slate-500 text-sm truncate">{tenant?.name}</p>
            </div>
          )}
          <Button
            variant="ghost"
            className={cn(
              "text-slate-400 hover:text-white hover:bg-slate-800",
              collapsed ? "w-full justify-center" : "w-full justify-start"
            )}
            onClick={handleLogout}
            data-testid="ops-logout-btn"
          >
            <LogOut className="w-5 h-5" />
            {!collapsed && <span className="ml-2">Logout</span>}
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        <Outlet />
      </main>
    </div>
  );
}
